import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../../components/ui/button';
import {
  Home, ChevronLeft, ChevronRight, X, Presentation as PresentIcon,
  CheckCircle2, XCircle, Sparkles, Bike,
} from 'lucide-react';
import { cours } from '../../data/cours/deuxiemePlateauA2';
import useSeo from '../../hooks/useSeo';
import { LightboxProvider, LightboxImage } from '../../components/LightboxGallery';
import { FullscreenOverlay, enterFullscreen } from '../../components/FullscreenMode';
import { Maximize2 } from 'lucide-react';
import PlateauSessionSummary from '../../components/plateau/PlateauSessionSummary';

function Retenir({ text }) {
  if (!text) return null;
  return (
    <div className="mt-6 rounded-xl border border-red-500/30 bg-gradient-to-br from-red-600/10 via-red-500/5 to-transparent p-4 sm:p-5 flex items-start gap-3">
      <div className="flex-shrink-0 mt-0.5 w-8 h-8 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center">
        <Sparkles className="w-4 h-4 text-red-400" />
      </div>
      <div>
        <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400 mb-1">À retenir</div>
        <p className="text-sm sm:text-base font-black leading-snug text-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{text}</p>
      </div>
    </div>
  );
}

function Section({ section, big = false }) {
  const numSize = big ? 'text-7xl md:text-8xl' : 'text-5xl md:text-6xl';
  const titleSize = big ? 'text-2xl md:text-4xl' : 'text-xl md:text-2xl';
  return (
    <section className="c2r-cours-section" data-testid={`section-${section.num}`}>
      <div className="flex items-start gap-5 mb-5">
        <span className={`${numSize} font-black leading-none text-red-500/25`} style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{section.num}</span>
        <div className="flex-1 pt-1">
          <p className="text-[10px] md:text-[11px] font-black uppercase tracking-[0.22em] text-red-500 mb-2">{section.kicker}</p>
          <h2 className={`${titleSize} font-extrabold leading-tight tracking-tight text-foreground ${section.image ? 'sr-only' : ''}`} style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{section.title}</h2>
        </div>
      </div>
      {section.image && <LightboxImage src={section.image} alt={section.imageAlt || section.title} caption={section.title} testId={`section-image-${section.num}`} />}
      {section.lead && <p className="text-[15px] md:text-base leading-relaxed text-muted-foreground mb-4">{section.lead}</p>}
      {section.type === 'timeline' && section.steps && (
        <div className="space-y-3 mb-4">
          {section.steps.map((s, i) => (
            <div key={i} className="flex items-start gap-4 p-4 rounded-xl border border-white/10 bg-black/20">
              <div className="flex-shrink-0 w-14 h-14 rounded-full bg-red-500/15 border-2 border-red-500/40 flex items-center justify-center text-red-400 font-black text-xs uppercase text-center leading-tight px-1">{s.time}</div>
              <div className="flex-1 pt-1">
                <div className="text-[11px] font-black uppercase tracking-widest text-red-400 mb-1">{s.label}</div>
                <p className="text-sm text-foreground/90 leading-relaxed">{s.desc}</p>
              </div>
            </div>
          ))}
        </div>
      )}
      {section.type === 'list' && section.list && (
        <div className="grid sm:grid-cols-2 gap-3 mb-4">
          {section.list.map((item, i) => (
            <div key={i} className="p-4 rounded-xl border border-white/10 bg-black/20">
              <div className="text-[11px] font-black uppercase tracking-widest text-red-400 mb-1">{item.label}</div>
              <p className="text-sm text-foreground/90 leading-relaxed">{item.desc}</p>
            </div>
          ))}
        </div>
      )}
      {section.type === 'cards3' && section.cards && (
        <div className="grid md:grid-cols-3 gap-3 mb-4">
          {section.cards.map((c, i) => (
            <div key={i} className="p-4 rounded-xl border border-white/10 bg-gradient-to-br from-red-600/10 to-transparent">
              <div className="text-sm font-black uppercase tracking-wide text-red-400 mb-2">{c.title}</div>
              <p className="text-sm text-foreground/90 leading-relaxed">{c.desc}</p>
            </div>
          ))}
        </div>
      )}
      {section.type === 'bilan' && (
        <>
          <div className="rounded-xl border border-white/10 bg-black/20 p-4 mb-4">
            <div className="text-[11px] font-black uppercase tracking-widest text-red-400 mb-3">Est-ce que je sais...</div>
            <ul className="space-y-2">
              {section.questions.map((q, i) => (
                <li key={i} className="flex items-start gap-2 text-sm text-foreground/90">
                  <CheckCircle2 className="w-4 h-4 text-emerald-400 flex-shrink-0 mt-0.5" />
                  <span>{q}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="grid md:grid-cols-3 gap-3 mb-4">
            {section.blocks.map((b, i) => {
              const c = b.color === 'green' ? 'emerald' : (b.color === 'amber' ? 'amber' : 'red');
              return (
                <div key={i} className={`p-4 rounded-xl border border-${c}-500/40 bg-${c}-950/20 text-center`}>
                  <div className={`text-xs font-black uppercase tracking-widest text-${c}-400`}>{b.title}</div>
                </div>
              );
            })}
          </div>
        </>
      )}
      {section.body && <p className="text-sm text-muted-foreground leading-relaxed mb-4 italic">{section.body}</p>}
      <Retenir text={section.retenir} />
    </section>
  );
}

function PresentationMode({ onClose }) {
  const [i, setI] = useState(0);
  const total = cours.sections.length;
  const next = useCallback(() => setI(v => Math.min(v + 1, total - 1)), [total]);
  const prev = useCallback(() => setI(v => Math.max(v - 1, 0)), []);
  useEffect(() => {
    const h = (e) => {
      if (e.key === 'ArrowRight' || e.key === ' ') next();
      else if (e.key === 'ArrowLeft') prev();
      else if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', h);
    return () => window.removeEventListener('keydown', h);
  }, [next, prev, onClose]);
  const s = cours.sections[i];
  return (
    <div className="fixed inset-0 z-[9999] bg-black" data-testid="presentation-mode">
      <div className="absolute top-4 right-4 flex items-center gap-2 z-10">
        <span className="text-white/60 text-sm font-mono">{i + 1} / {total}</span>
        <Button variant="ghost" size="icon" onClick={onClose} className="text-white hover:bg-white/10"><X className="w-5 h-5" /></Button>
      </div>
      <div className="h-full overflow-y-auto px-6 md:px-16 py-16 max-w-5xl mx-auto">
        <Section section={s} big />
      </div>
      <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-3">
        <Button variant="outline" onClick={prev} disabled={i === 0} className="bg-white/5 border-white/20 text-white hover:bg-white/10"><ChevronLeft className="w-4 h-4 mr-1" /> Précédent</Button>
        <Button onClick={next} disabled={i === total - 1} className="bg-red-600 hover:bg-red-500 text-white">Suivant <ChevronRight className="w-4 h-4 ml-1" /></Button>
      </div>
    </div>
  );
}

export default function DeuxiemePlateauA2() {
  const [present, setPresent] = useState(false);
  useSeo({ title: `${cours.title} — Cours premium Code 2 Roues`, description: cours.intro });
  return (
    <LightboxProvider>
    <div className="min-h-screen bg-background text-foreground pb-20 print:pb-0">
      <div className="sticky top-0 z-40 border-b border-white/10 bg-background/95 backdrop-blur print:hidden">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center gap-3">
          <Button asChild variant="outline" size="sm" data-testid="btn-accueil"><Link to="/"><Home className="w-4 h-4 mr-1" /> Accueil</Link></Button>
          <div className="flex-1 text-center"><div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-500">Cours · Plateau A2</div></div>
          <Button asChild variant="ghost" size="sm"><Link to="/livret-plateau"><Bike className="w-4 h-4 mr-1" /> Livret plateau</Link></Button>
          <Button onClick={() => enterFullscreen(() => setPresent(true))} size="sm" className="bg-red-600 hover:bg-red-500" data-testid="btn-present"><Maximize2 className="w-4 h-4 mr-1" /> Plein écran</Button>
        </div>
      </div>
      <article className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
        {cours.cover && (
          <div className="mb-8 rounded-2xl overflow-hidden border border-red-500/30 shadow-2xl shadow-red-500/10 aspect-[3/2] bg-black" data-testid="cours-cover">
            <img src={cours.cover} alt={`${cours.title} — poster`} className="w-full h-full object-cover" loading="eager" />
          </div>
        )}
        <header className="mb-10 text-center" data-testid="cours-hero">
          <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500/15 border border-red-500/30 text-[10px] font-black uppercase tracking-widest text-red-400 mb-4">
            <Bike className="w-3 h-3" /> Cours premium · Séance plateau 2
          </div>
          <h1 className="text-3xl sm:text-5xl md:text-6xl font-black leading-tight mb-3" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{cours.title}</h1>
          <p className="text-lg md:text-xl text-red-500 font-black tracking-wide mb-6">{cours.subtitle}</p>
          <div className="max-w-2xl mx-auto p-4 rounded-xl border border-red-500/30 bg-red-500/5">
            <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400 mb-2">Fil conducteur</div>
            <div className="text-sm md:text-base font-black text-foreground" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>{cours.mantra}</div>
          </div>
          <p className="mt-6 text-sm text-muted-foreground max-w-3xl mx-auto leading-relaxed">{cours.intro}</p>
        </header>
        <nav className="mb-10 p-4 rounded-xl border border-white/10 bg-black/20" data-testid="sommaire">
          <div className="text-[11px] font-black uppercase tracking-widest text-red-400 mb-3">Sommaire · 14 chapitres</div>
          <div className="grid sm:grid-cols-2 gap-1.5">
            {cours.sections.map((s) => (
              <a key={s.num} href={`#section-${s.num}`} className="text-sm text-foreground/80 hover:text-red-400 transition py-0.5">
                <span className="font-black text-red-500/60 mr-2">{s.num}</span>{s.title}
              </a>
            ))}
          </div>
        </nav>
        <div className="space-y-10">
          {cours.sections.map((s) => <div key={s.num} id={`section-${s.num}`}><Section section={s} /></div>)}
        </div>
        <div className="mt-12 p-6 rounded-2xl border-2 border-red-500/30 bg-gradient-to-br from-red-600/15 to-transparent text-center">
          <div className="text-[10px] font-black uppercase tracking-[0.22em] text-red-400 mb-2">Continue ton apprentissage plateau</div>
          <h3 className="text-xl md:text-2xl font-black text-foreground mb-4" style={{ fontFamily: "'Playfair Display', Georgia, serif" }}>Retrouve ta progression sur ton livret plateau</h3>
          <Button asChild className="bg-red-600 hover:bg-red-500 text-white"><Link to="/livret-plateau">Ouvrir mon livret plateau</Link></Button>
        </div>

        {/* Résumé auto-école — mapping séance 2 : P1 (rappels) + P2 (coordination) */}
        <PlateauSessionSummary
          courseSlug="deuxieme-plateau-a2"
          courseTitle={cours.title}
          courseCode="Plateau n°2"
          mantra={cours.mantra}
          subtitle={cours.subtitle}
          duree={cours.duree}
          defaultPhaseIds={['p1', 'p2']}
        />
      </article>
      {present && <FullscreenOverlay onExit={() => setPresent(false)} />}
    </div>
    </LightboxProvider>
  );
}
